import React, { useState, useEffect } from 'react';
import {
  CheckCircle, XCircle, Phone, MapPin, Star, LayoutGrid, LayoutList,
  Clock, AlertCircle, Building2, RotateCcw, Search, Calendar, Edit3,
} from 'lucide-react';
import apiService from '../../apiService';
import { useLiveRefresh } from '../../liveRefresh';
import Img from '../../components/Img';
import CityFilter from '../../components/CityFilter';

/* ─── helpers ─────────────────────────────────────────────── */
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const STATUS_CONFIG = {
  approved:  { label: 'Validé',      bg: '#ECFDF5', color: '#10B981', icon: <CheckCircle size={12}/> },
  pending:   { label: 'En attente',  bg: '#fef9c3', color: '#ca8a04', icon: <Clock size={12}/> },
  rejected:  { label: 'Refusé',      bg: '#fee2e2', color: '#dc2626', icon: <XCircle size={12}/> },
  suspended: { label: 'Suspendu',    bg: '#F4F5F7', color: '#5C636E', icon: <AlertCircle size={12}/> },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:'4px',
      padding:'3px 8px', borderRadius:'12px', fontSize:'0.72rem', fontWeight:'700',
      background: cfg.bg, color: cfg.color, whiteSpace:'nowrap',
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

/* ─── action buttons per status ──────────────────────────── */
function ActionButtons({ supplier, actionLoading, onValidate }) {
  const { status, id } = supplier;
  const busy = actionLoading === id;

  if (status === 'approved') return null; // pas d'action sur un validé

  return (
    <div style={{ display:'flex', gap:'0.5rem' }} onClick={e => e.stopPropagation()}>
      {/* Valider — toujours disponible si pas approved */}
      <button
        disabled={busy}
        onClick={() => onValidate(id, true)}
        style={{
          flex:1, padding:'0.45rem 0.6rem', borderRadius:'10px', border:'none',
          background:'#10B981', color:'white',
          fontWeight:'600', fontSize:'0.8rem', cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1, display:'flex', alignItems:'center', justifyContent:'center', gap:'5px',
        }}
      >
        <CheckCircle size={14}/> {busy ? '...' : 'Valider'}
      </button>

      {/* Refuser — seulement si pending ; si déjà rejected montrer "Remettre en attente" */}
      {status === 'pending' && (
        <button
          disabled={busy}
          onClick={() => onValidate(id, false)}
          style={{
            flex:1, padding:'0.45rem 0.6rem', borderRadius:'10px', border:'2px solid #fecaca',
            background:'white', color:'#DC2626',
            fontWeight:'600', fontSize:'0.8rem', cursor: busy ? 'wait' : 'pointer',
            opacity: busy ? 0.6 : 1, display:'flex', alignItems:'center', justifyContent:'center', gap:'5px',
          }}
        >
          <XCircle size={14}/> Refuser
        </button>
      )}
    </div>
  );
}

/* ─── avatar : photo du commerce, sinon initiale colorée selon le statut ─── */
function SupplierAvatar({ supplier, size }) {
  const tone = supplier.status === 'approved' ? ['#ECFDF5', '#10B981']
    : supplier.status === 'rejected' ? ['#fee2e2', '#dc2626'] : ['#fef9c3', '#ca8a04'];
  if (supplier.logo) {
    return (
      <Img src={supplier.logo} thumb={supplier.logo_thumb} alt=""
        style={{ width: size, height: size, flexShrink: 0, borderRadius: '50%', objectFit: 'cover', border: '1px solid #E9EBEF' }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, flexShrink: 0, borderRadius: '50%', background: tone[0], color: tone[1],
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size >= 48 ? '1.1rem' : '0.9rem', fontWeight: '700',
    }}>
      {(supplier.business_name || supplier.owner_name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

/* ─── card (grid view) ────────────────────────────────────── */
function SupplierCard({ supplier, actionLoading, onValidate, onClick, onEditOwner }) {
  return (
    <div
      onClick={onClick}
      style={{
        background:'white', borderRadius:'16px', padding:'1.25rem',
        boxShadow:'0 1px 3px rgba(0,0,0,0.08)', cursor:'pointer',
        border: supplier.status === 'rejected' ? '1px solid #fecaca' : '1px solid transparent',
        transition:'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.08)'; }}
    >
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.75rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <SupplierAvatar supplier={supplier} size={48} />
          <div>
            <h3 style={{ margin:0, fontWeight:'600', fontSize:'1rem', color:'#1E2229' }}>
              {supplier.business_name || supplier.owner_name}
            </h3>
            <p style={{ margin:0, fontSize:'0.75rem', color:'#9AA0A8', textTransform:'capitalize' }}>
              {supplier.business_type || '—'}
            </p>
          </div>
        </div>
        <StatusBadge status={supplier.status} />
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem', fontSize:'0.85rem', color:'#5C636E', marginBottom:'0.75rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.35rem' }}><Phone size={13}/> {supplier.phone || '—'}</div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.35rem' }}><MapPin size={13}/> {supplier.address || 'Non renseigné'}</div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.35rem' }}><Star size={13} color="#eab308"/> {supplier.rating || '—'}</div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.35rem' }}><Calendar size={13}/> Inscrit le {fmtDate(supplier.created_at)}</div>
      </div>

      <ActionButtons supplier={supplier} actionLoading={actionLoading} onValidate={onValidate} />

      {onEditOwner && supplier.owner_id && (
        <button
          onClick={e => { e.stopPropagation(); onEditOwner(supplier.owner_id); }}
          style={{
            marginTop:'0.5rem', width:'100%', padding:'0.45rem 0.6rem', borderRadius:'10px',
            border:'1px solid #bae6fd', background:'#FEE9E2', color:'#C9350F',
            fontWeight:'600', fontSize:'0.8rem', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap:'5px',
          }}
        >
          <Edit3 size={13}/> Modifier le propriétaire
        </button>
      )}
    </div>
  );
}

/* ─── row (list view) ────────────────────────────────────── */
function SupplierRow({ supplier, actionLoading, onValidate, onClick, onEditOwner, isLast }) {
  return (
    <div
      onClick={onClick}
      style={{
        display:'grid',
        gridTemplateColumns:'1fr 140px 130px 100px 120px',
        gap:'0.75rem',
        alignItems:'center',
        padding:'0.85rem 1rem',
        borderBottom: isLast ? 'none' : '1px solid #F4F5F7',
        cursor:'pointer',
        transition:'background 0.1s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background='#FFFFFF'; }}
      onMouseLeave={e => { e.currentTarget.style.background='transparent'; }}
    >
      {/* Nom + type */}
      <div style={{ display:'flex', alignItems:'center', gap:'0.65rem', minWidth:0 }}>
        <SupplierAvatar supplier={supplier} size={36} />
        <div style={{ minWidth:0 }}>
          <div style={{ fontWeight:'600', fontSize:'0.9rem', color:'#1E2229', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
            {supplier.business_name || supplier.owner_name}
          </div>
          <div style={{ fontSize:'0.75rem', color:'#9AA0A8' }}>
            <span style={{ textTransform:'capitalize' }}>{supplier.business_type || '—'}</span> · Inscrit le {fmtDate(supplier.created_at)}
          </div>
        </div>
      </div>

      {/* Téléphone */}
      <div style={{ fontSize:'0.83rem', color:'#5C636E', display:'flex', alignItems:'center', gap:'4px' }}>
        <Phone size={12}/> {supplier.phone || '—'}
      </div>

      {/* Adresse */}
      <div style={{ fontSize:'0.83rem', color:'#5C636E', display:'flex', alignItems:'center', gap:'4px', overflow:'hidden' }}>
        <MapPin size={12}/> <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{supplier.address || '—'}</span>
      </div>

      {/* Statut */}
      <div><StatusBadge status={supplier.status} /></div>

      {/* Actions */}
      <div onClick={e => e.stopPropagation()} style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
        <ActionButtons supplier={supplier} actionLoading={actionLoading} onValidate={onValidate} />
        {onEditOwner && supplier.owner_id && (
          <button
            onClick={() => onEditOwner(supplier.owner_id)}
            title="Modifier le propriétaire"
            style={{
              padding:'0.4rem 0.5rem', borderRadius:'8px', border:'1px solid #bae6fd',
              background:'#FEE9E2', color:'#C9350F', fontWeight:'600', fontSize:'0.75rem',
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'4px',
            }}
          >
            <Edit3 size={12}/> Propriétaire
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── main page ───────────────────────────────────────────── */
const FILTERS = [
  { key:'all',      label:'Tous',       color:'#F54119' },
  { key:'pending',  label:'En attente', color:'#FFB900' },
  { key:'approved', label:'Validés',    color:'#10B981' },
  { key:'rejected', label:'Refusés',    color:'#DC2626' },
];

/* ─── Promotions boutiques en cours (cloisonnées ville côté serveur) ─── */
function PromosPanel() {
  const [promos, setPromos] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const loadPromos = async () => {
    try {
      const r = await apiService.get('/delivery/promos/admin/active', { userType: 'admin' });
      setPromos(r.promos || []);
    } catch { /* silencieux */ }
  };
  useEffect(() => { loadPromos(); }, []);

  const describe = (p) => {
    if (p.type === 'percent') return `-${p.percent} % ${p.category_id ? 'sur un rayon' : 'sur toute la boutique'}`;
    if (p.type === 'bogo') return `${p.buy_qty} acheté(s) = ${p.free_qty} offert(s)${(p.product_ids || []).length ? ` · ${p.product_ids.length} produit(s)` : ''}`;
    return `-${(p.discount_amount || 0).toLocaleString('fr-FR')} FC dès ${(p.min_amount || 0).toLocaleString('fr-FR')} FC`;
  };

  const disable = async (p) => {
    if (!window.confirm(`Désactiver la promotion de « ${p.business_name} » ?\nElle prendra fin immédiatement.`)) return;
    setBusy(p.promo_id);
    try {
      await apiService.patch(`/delivery/promos/admin/${p.promo_id}/disable`, {}, { userType: 'admin' });
      await loadPromos();
    } catch (err) {
      alert(err.message || 'Erreur');
    } finally {
      setBusy(null);
    }
  };

  if (!promos.length) return null;
  const now = new Date();
  return (
    <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:'14px', padding:'1rem 1.25rem', marginBottom:'1.25rem' }}>
      <button onClick={() => setOpen(o => !o)} style={{ background:'none', border:'none', cursor:'pointer', padding:0, width:'100%', textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontWeight:800, color:'#9A3412', fontSize:'0.95rem' }}>🏷️ Promotions boutiques en cours ({promos.length})</span>
        <span style={{ color:'#9A3412', fontSize:'0.8rem' }}>{open ? 'Replier ▲' : 'Voir ▼'}</span>
      </button>
      {open && (
        <div style={{ marginTop:'0.75rem', display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {promos.map(p => (
            <div key={p.promo_id} style={{ display:'flex', alignItems:'center', gap:'0.75rem', background:'white', borderRadius:'10px', padding:'0.6rem 0.9rem', flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:220 }}>
                <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#1E2229' }}>
                  {p.business_name} <span style={{ color:'#9AA0A8', fontWeight:600 }}>· {p.city_slug || '—'}</span>
                </div>
                <div style={{ fontSize:'0.82rem', color:'#5C636E' }}>
                  {describe(p)}{p.title ? ` — « ${p.title} »` : ''}
                  {' · '}{new Date(p.starts_at) > now ? 'à venir' : 'en cours'} jusqu'au {new Date(p.ends_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
              <button onClick={() => disable(p)} disabled={busy === p.promo_id}
                style={{ border:'1px solid #FECACA', background:'#FEF2F2', color:'#B91C1C', borderRadius:'8px', padding:'6px 12px', fontWeight:700, fontSize:'0.8rem', cursor:'pointer' }}>
                {busy === p.promo_id ? '…' : 'Désactiver'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DeliverySuppliersPage({ onSelectSupplier, onEditOwner }) {
  const [suppliers, setSuppliers]     = useState([]);
  const [filter, setFilter]           = useState('all');
  const [search, setSearch]           = useState('');
  const [cityFilter, setCityFilter]   = useState(''); // '' = toutes les villes (filtrage serveur)
  const [viewMode, setViewMode]       = useState('grid'); // 'grid' | 'list'
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchSuppliers = async () => {
    setLoading(true);
    setError('');
    try {
      // Paramètres dans l'URL : apiService.get de l'admin ignore l'objet de paramètres.
      const response = await apiService.get(`/admin/delivery/suppliers${cityFilter ? `?city_slug=${encodeURIComponent(cityFilter)}` : ''}`, { userType: 'admin' });
      if (response.success) setSuppliers(response.data || []);
    } catch {
      setError('Impossible de charger les fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSuppliers(); }, [cityFilter]);
  useLiveRefresh(() => fetchSuppliers(), ['supplier_registration']); // nouveau commerce → liste à jour sans recharger

  const handleValidate = async (supplierId, approved) => {
    setActionLoading(supplierId);
    setError('');
    setSuccess('');
    try {
      const res = await apiService.patch(
        `/admin/delivery/suppliers/${supplierId}/validate`,
        { is_validated: approved },
        { userType: 'admin' }
      );
      setSuccess(res.message || (approved ? 'Fournisseur validé ✅' : 'Fournisseur refusé'));
      await fetchSuppliers();
    } catch (err) {
      setError(err.message || 'Erreur lors de la validation');
    } finally {
      setActionLoading(null);
    }
  };

  /* counts per status */
  const counts = suppliers.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});

  const q = search.trim().toLowerCase();
  const filtered = suppliers
    .filter(s => filter === 'all' || s.status === filter)
    .filter(s => !q || [s.business_name, s.owner_name, s.phone, s.address, s.supplier_id, s.business_type]
      .some(v => (v || '').toString().toLowerCase().includes(q)))
    // Tri par date d'inscription (plus récents en premier)
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'200px' }}>
      <span style={{ fontSize:'1.1rem', color:'#9AA0A8' }}>⏳ Chargement...</span>
    </div>
  );

  return (
    <div>
      <PromosPanel />
      {/* ── Header ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.5rem' }}>
        <div>
          <h2 style={{ margin:0, fontSize:'1.5rem', fontWeight:'700', color:'#1E2229' }}>🏪 Fournisseurs Livraison</h2>
          <p style={{ margin:'0.25rem 0 0', fontSize:'0.875rem', color:'#9AA0A8' }}>{suppliers.length} fournisseur(s)</p>
        </div>

        {/* Vue grid / liste */}
        <div style={{ display:'flex', gap:'4px', background:'#F4F5F7', padding:'4px', borderRadius:'10px' }}>
          <button
            onClick={() => setViewMode('grid')}
            title="Vue carte"
            style={{
              padding:'6px 10px', borderRadius:'7px', border:'none', cursor:'pointer',
              background: viewMode === 'grid' ? 'white' : 'transparent',
              color: viewMode === 'grid' ? '#1E2229' : '#9AA0A8',
              boxShadow: viewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              display:'flex', alignItems:'center',
            }}
          >
            <LayoutGrid size={17}/>
          </button>
          <button
            onClick={() => setViewMode('list')}
            title="Vue liste"
            style={{
              padding:'6px 10px', borderRadius:'7px', border:'none', cursor:'pointer',
              background: viewMode === 'list' ? 'white' : 'transparent',
              color: viewMode === 'list' ? '#1E2229' : '#9AA0A8',
              boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              display:'flex', alignItems:'center',
            }}
          >
            <LayoutList size={17}/>
          </button>
        </div>
      </div>

      {/* ── Toasts ── */}
      {error   && <div style={{ background:'#fef2f2', color:'#dc2626', padding:'0.75rem 1rem', borderRadius:'8px', marginBottom:'1rem', fontSize:'0.875rem' }}>{error}</div>}
      {success && <div style={{ background:'#ECFDF5', color:'#10B981', padding:'0.75rem 1rem', borderRadius:'8px', marginBottom:'1rem', fontSize:'0.875rem' }}>{success}</div>}

      {/* ── Recherche + Ville ── */}
      <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', alignItems:'center', marginBottom:'1rem' }}>
      <div style={{ position:'relative', flex:'1 1 320px', maxWidth:'420px' }}>
        <Search size={16} style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', color:'#9AA0A8' }} />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un fournisseur (nom, téléphone, adresse…)"
          style={{
            width:'100%', padding:'0.6rem 2.2rem 0.6rem 2.2rem', borderRadius:'12px',
            border:'1px solid #E9EBEF', fontSize:'0.875rem', color:'#1E2229', outline:'none',
          }}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            title="Effacer"
            style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', border:'none', background:'transparent', cursor:'pointer', color:'#9AA0A8', display:'flex' }}
          >
            <XCircle size={16} />
          </button>
        )}
      </div>
      <CityFilter value={cityFilter} onChange={setCityFilter} className="" style={{ background: 'white', border: '1px solid #E9EBEF', borderRadius: '12px', padding: '0.5rem 0.75rem', fontSize: '0.875rem', cursor: 'pointer', outline: 'none' }} />
      </div>

      {/* ── Filtres ── */}
      <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1.25rem', flexWrap:'wrap' }}>
        {FILTERS.map(f => {
          const count = f.key === 'all' ? suppliers.length : (counts[f.key] || 0);
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding:'0.45rem 1rem', borderRadius:'12px',
                border: active ? 'none' : '1px solid #E9EBEF',
                background: active ? f.color : 'white',
                color: active ? 'white' : '#5C636E',
                fontWeight:'600', fontSize:'0.875rem', cursor:'pointer',
                display:'flex', alignItems:'center', gap:'6px',
                transition:'all 0.15s',
              }}
            >
              {f.label}
              <span style={{
                background: active ? 'rgba(255,255,255,0.25)' : '#F4F5F7',
                color: active ? 'white' : '#5C636E',
                borderRadius:'20px', padding:'1px 7px', fontSize:'0.75rem', fontWeight:'700',
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Vue GRILLE ── */}
      {viewMode === 'grid' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))', gap:'1rem' }}>
          {filtered.map(supplier => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              actionLoading={actionLoading}
              onValidate={handleValidate}
              onClick={() => onSelectSupplier && onSelectSupplier(supplier.id)}
              onEditOwner={onEditOwner}
            />
          ))}
        </div>
      )}

      {/* ── Vue LISTE ── */}
      {viewMode === 'list' && filtered.length > 0 && (
        <div style={{ background:'white', borderRadius:'16px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)', overflow:'hidden' }}>
          {/* Entête */}
          <div style={{
            display:'grid',
            gridTemplateColumns:'1fr 140px 130px 100px 120px',
            gap:'0.75rem',
            padding:'0.65rem 1rem',
            background:'#FFFFFF',
            borderBottom:'1px solid #E9EBEF',
            fontSize:'0.75rem', fontWeight:'700', color:'#9AA0A8', textTransform:'uppercase', letterSpacing:'0.04em',
          }}>
            <span>Fournisseur</span>
            <span>Téléphone</span>
            <span>Adresse</span>
            <span>Statut</span>
            <span>Actions</span>
          </div>

          {filtered.map((supplier, idx) => (
            <SupplierRow
              key={supplier.id}
              supplier={supplier}
              actionLoading={actionLoading}
              onValidate={handleValidate}
              onClick={() => onSelectSupplier && onSelectSupplier(supplier.id)}
              onEditOwner={onEditOwner}
              isLast={idx === filtered.length - 1}
            />
          ))}
        </div>
      )}

      {/* ── Vide ── */}
      {filtered.length === 0 && !loading && (
        <div style={{ textAlign:'center', padding:'3rem', color:'#9AA0A8' }}>
          <p style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>🏪</p>
          <p>
            {q
              ? `Aucun fournisseur ne correspond à « ${search.trim()} »`
              : (filter !== 'all'
                  ? `Aucun fournisseur avec le statut « ${FILTERS.find(f=>f.key===filter)?.label} »`
                  : 'Aucun fournisseur trouvé')}
          </p>
        </div>
      )}
    </div>
  );
}
